// src/components/setup/SetupPage.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button,
    Stepper, Step, StepLabel, Alert, Divider,
    InputAdornment, CircularProgress
} from '@mui/material';
import { Email, Verified } from '@mui/icons-material';
import api from '../../services/api';

const steps = [
    'Admin Account',
    'Verify Email',
    'Store Info',
    'Confirm'
];

const SetupPage = ({ onComplete }) => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [emailVerified, setEmailVerified] = useState(false);

    const [form, setForm] = useState({
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        adminPasswordConfirm: '',
        verificationCode: '',
        storeName: '',
        storeCode: '',
        storeEmail: '',
        storePhone: '',
        storeAddress: '',
    });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError(null);
    };

    const handleSendCode = async () => {
        setSendingCode(true);
        setError(null);
        setSuccess(null);
        try {
            await api.post('/setup/send-code', { email: form.adminEmail });
            setSuccess(`Verification code sent to ${form.adminEmail}`);
            setActiveStep(1);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send code');
        } finally {
            setSendingCode(false);
        }
    };

    const handleVerifyCode = async () => {
        setLoading(true);
        setError(null);
        try {
            await api.post('/setup/verify-code', {
                email: form.adminEmail,
                code: form.verificationCode
            });
            setEmailVerified(true);
            setSuccess('Email verified successfully!');
            setActiveStep(2);
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid or expired code');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        setError(null);
        setSuccess(null);

        if (activeStep === 0) {
            if (!form.adminName || !form.adminEmail || !form.adminPassword) {
                setError('Please fill all required fields');
                return;
            }
            if (form.adminPassword !== form.adminPasswordConfirm) {
                setError('Passwords do not match');
                return;
            }
            if (form.adminPassword.length < 8) {
                setError('Password must be at least 8 characters');
                return;
            }
            handleSendCode();
            return;
        }

        if (activeStep === 2) {
            if (!form.storeName || !form.storeCode) {
                setError('Store name and code are required');
                return;
            }
        }

        setActiveStep(prev => prev + 1);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            await api.post('/setup/initialize', {
                adminName:     form.adminName,
                adminEmail:    form.adminEmail,
                adminPassword: form.adminPassword,
                storeName:     form.storeName,
                storeCode:     form.storeCode,
                storeEmail:    form.storeEmail,
                storePhone:    form.storePhone,
                storeAddress:  form.storeAddress,
            });

            setSuccess('✅ Setup completed successfully! Redirecting to login...');
            onComplete();
            setTimeout(() => navigate('/login'), 2000);

        } catch (err) {
            setError(err.response?.data?.message || 'Setup failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #08519c 0%, #87CEEB 100%)',
            p: 2
        }}>
            <Paper elevation={6} sx={{ p: 4, width: '100%', maxWidth: 560, borderRadius: 2 }}>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight={700} color="primary">
                        INVOTRIX
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        First time setup — let's get you started
                    </Typography>
                </Box>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {/* Step 0 — Admin Account */}
                {activeStep === 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Create Super Admin Account
                        </Typography>
                        <TextField fullWidth label="Full Name *"
                            name="adminName" value={form.adminName}
                            onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Email *" type="email"
                            name="adminEmail" value={form.adminEmail}
                            onChange={handleChange} margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Email color="action" />
                                    </InputAdornment>
                                )
                            }} />
                        <TextField fullWidth label="Password *" type="password"
                            name="adminPassword" value={form.adminPassword}
                            onChange={handleChange} margin="normal"
                            helperText="Minimum 8 characters" />
                        <TextField fullWidth label="Confirm Password *" type="password"
                            name="adminPasswordConfirm" value={form.adminPasswordConfirm}
                            onChange={handleChange} margin="normal" />
                    </Box>
                )}

                {/* Step 1 — Email Verification */}
                {activeStep === 1 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Verify Your Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            We sent a 6-digit code to <strong>{form.adminEmail}</strong>
                        </Typography>
                        <TextField
                            fullWidth
                            label="Verification Code *"
                            name="verificationCode"
                            value={form.verificationCode}
                            onChange={handleChange}
                            margin="normal"
                            inputProps={{ maxLength: 6, style: { letterSpacing: 8, fontSize: 24, textAlign: 'center' } }}
                            placeholder="000000"
                        />
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleSendCode}
                            disabled={sendingCode}
                            sx={{ mt: 1 }}
                        >
                            {sendingCode ? 'Sending...' : 'Resend code'}
                        </Button>
                    </Box>
                )}

                {/* Step 2 — Store Info */}
                {activeStep === 2 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Store Information
                        </Typography>
                        <TextField fullWidth label="Store Name *"
                            name="storeName" value={form.storeName}
                            onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Store Code *"
                            name="storeCode" value={form.storeCode}
                            onChange={handleChange} margin="normal"
                            helperText="Unique identifier e.g. CHC-BLIDA (cannot be changed later)"
                            inputProps={{ style: { textTransform: 'uppercase' } }} />
                        <TextField fullWidth label="Store Email"
                            name="storeEmail" value={form.storeEmail}
                            onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Phone"
                            name="storePhone" value={form.storePhone}
                            onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Address"
                            name="storeAddress" value={form.storeAddress}
                            onChange={handleChange} margin="normal" />
                    </Box>
                )}

                {/* Step 3 — Confirm */}
                {activeStep === 3 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Confirm Setup
                        </Typography>

                        <Alert severity="success" icon={<Verified />} sx={{ mb: 2 }}>
                            Email verified: {form.adminEmail}
                        </Alert>

                        <Typography variant="subtitle2" color="text.secondary">
                            Admin Account
                        </Typography>
                        <Typography fontWeight={500}>{form.adminName}</Typography>
                        <Typography color="text.secondary">{form.adminEmail}</Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" color="text.secondary">
                            Store
                        </Typography>
                        <Typography fontWeight={500}>
                            {form.storeName} ({form.storeCode.toUpperCase()})
                        </Typography>
                        {form.storeEmail   && <Typography color="text.secondary">{form.storeEmail}</Typography>}
                        {form.storePhone   && <Typography color="text.secondary">{form.storePhone}</Typography>}
                        {form.storeAddress && <Typography color="text.secondary">{form.storeAddress}</Typography>}
                    </Box>
                )}

                {/* Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                        disabled={activeStep === 0 || activeStep === 1}
                        onClick={() => {
                            setError(null);
                            setSuccess(null);
                            setActiveStep(prev => prev - 1);
                        }}
                    >
                        Back
                    </Button>

                    {/* Step 0 — Send Code */}
                    {activeStep === 0 && (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={sendingCode}
                            startIcon={sendingCode ? <CircularProgress size={16} /> : null}
                        >
                            {sendingCode ? 'Sending...' : 'Send Verification Code'}
                        </Button>
                    )}

                    {/* Step 1 — Verify Code */}
                    {activeStep === 1 && (
                        <Button
                            variant="contained"
                            onClick={handleVerifyCode}
                            disabled={loading || form.verificationCode.length !== 6}
                            startIcon={loading ? <CircularProgress size={16} /> : null}
                        >
                            {loading ? 'Verifying...' : 'Verify Code'}
                        </Button>
                    )}

                    {/* Step 2 — Next */}
                    {activeStep === 2 && (
                        <Button variant="contained" onClick={handleNext}>
                            Next
                        </Button>
                    )}

                    {/* Step 3 — Complete */}
                    {activeStep === 3 && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : null}
                        >
                            {loading ? 'Setting up...' : 'Complete Setup'}
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default SetupPage;